import type {
  OpeningHours,
  OutletSettings,
  ReceiptSettings,
  UpdateOutletSettingsRequest,
} from "@kopi/contracts";
import { PAPER_WIDTHS, type PaperWidth, WEEKDAYS, type Weekday } from "@kopi/contracts/codes";
import { Badge } from "@kopi/ui/components/badge";
import { Button } from "@kopi/ui/components/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@kopi/ui/components/card";
import { Checkbox } from "@kopi/ui/components/checkbox";
import { Input } from "@kopi/ui/components/input";
import { Label } from "@kopi/ui/components/label";
import { OptionSelect } from "@kopi/ui/components/option-select";
import { toast } from "@kopi/ui/components/sonner";
import { Switch } from "@kopi/ui/components/switch";
import { type QueryClient, useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { type FormEvent, type ReactNode, useId, useState } from "react";
import { ActiveToggle } from "@/components/active-toggle";
import { Page, PageHeader } from "@/components/page-header";
import { outlets } from "@/i18n/messages/outlets";
import { type AreaTranslate, useMessages } from "@/i18n/provider";
import { canAt, canTenantWide } from "@/lib/access";
import { ApiRequestError, api, unwrap } from "@/lib/api";
import { meScopesQuery, outletSettingsQuery, outletsQuery } from "@/lib/queries";
import { TIMEZONE_OPTIONS } from "@/lib/timezones";

export const Route = createFileRoute("/_authed/outlets/$outletId")({
  staticData: {
    section: "preferences",
    parent: { label: "nav.settings", to: "/settings" },
    crumb: "page.outlet",
  },
  loader: ({ context, params }) => context.queryClient.ensureQueryData(outletSettingsQuery(params.outletId)),
  component: OutletSettingsPage,
});

const DAY_KEYS = {
  mon: "outlets.day.mon",
  tue: "outlets.day.tue",
  wed: "outlets.day.wed",
  thu: "outlets.day.thu",
  fri: "outlets.day.fri",
  sat: "outlets.day.sat",
  sun: "outlets.day.sun",
} as const satisfies Record<Weekday, keyof typeof outlets.id>;

function errorMessage(t: AreaTranslate<typeof outlets.id>, error: unknown): string {
  if (!(error instanceof ApiRequestError)) return t("common.saveOffline");
  switch (error.code) {
    case "validation_failed":
      return t("outlets.invalid");
    case "not_found":
      return t("outlets.notFound");
    case "forbidden":
      return t("outlets.ownerOnly");
    default:
      return t("common.saveFailed");
  }
}

function OutletSettingsPage() {
  const { t } = useMessages(outlets);
  const { outletId } = Route.useParams();
  const { data: outlet } = useSuspenseQuery(outletSettingsQuery(outletId));
  const { data: scopes } = useSuspenseQuery(meScopesQuery);

  return (
    <Page width="narrow">
      <PageHeader
        eyebrow={`${t("nav.settings")} · ${t("page.outlet")}`}
        title={
          <span className="flex flex-wrap items-center gap-3">
            {outlet.name}
            {!outlet.active && <Badge variant="outline">{t("common.inactive")}</Badge>}
          </span>
        }
        subtitle={t("outlets.code", { code: outlet.code })}
      />
      {/* Remount on reload so the form starts from the saved values. */}
      <SettingsForm key={outlet.id} outlet={outlet} editable={canAt(scopes, "outlets.settings", outlet.id)} />
      {canTenantWide(scopes, "outlets.manage") && <ActivationCard outlet={outlet} />}
    </Page>
  );
}

/** Caches the saved outlet and refreshes the outlet list (names and status appear there too). */
async function storeOutlet(queryClient: QueryClient, outlet: OutletSettings) {
  queryClient.setQueryData(outletSettingsQuery(outlet.id).queryKey, outlet);
  await queryClient.invalidateQueries({ queryKey: outletsQuery.queryKey });
}

function Field(props: { label: string; hint?: string; children: (id: string) => ReactNode }) {
  const id = useId();
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{props.label}</Label>
      {props.children(id)}
      {props.hint && <p className="text-muted-foreground text-xs">{props.hint}</p>}
    </div>
  );
}

/** One settings section: a card whose fields form a named group. */
function Section(props: { title: string; description?: string; children: ReactNode }) {
  const titleId = useId();
  return (
    <Card role="group" aria-labelledby={titleId}>
      <CardHeader>
        <CardTitle id={titleId}>{props.title}</CardTitle>
        {props.description && <CardDescription>{props.description}</CardDescription>}
      </CardHeader>
      <CardContent className="flex flex-col gap-4">{props.children}</CardContent>
    </Card>
  );
}

/** Read-only for staff without `outlets.settings` at this outlet. */
function SettingsForm(props: { outlet: OutletSettings; editable: boolean }) {
  const { t } = useMessages(outlets);
  const queryClient = useQueryClient();
  const [form, setForm] = useState(props.outlet);
  const [reason, setReason] = useState("");
  // The fieldset disables native inputs; Radix controls also get `disabled` so they look it.
  const readOnly = !props.editable;
  const set = <K extends keyof OutletSettings>(key: K, value: OutletSettings[K]) =>
    setForm((f) => ({ ...f, [key]: value }));
  const setReceipt = (change: Partial<ReceiptSettings>) => set("receipt", { ...form.receipt, ...change });
  const setDay = (day: keyof OpeningHours, value: OpeningHours[keyof OpeningHours]) =>
    set("openingHours", { ...form.openingHours, [day]: value });

  const save = useMutation({
    mutationFn: async () => {
      const body: UpdateOutletSettingsRequest = {
        name: form.name,
        phone: form.phone?.trim() || null,
        email: form.email?.trim() || null,
        address: form.address?.trim() || null,
        timezone: form.timezone,
        businessDayCutoff: form.businessDayCutoff,
        openingHours: form.openingHours,
        receipt: {
          ...form.receipt,
          // Only a printer with a real host goes through; an emptied field means "none".
          printer: form.receipt.printer?.host.trim()
            ? {
                host: form.receipt.printer.host.trim(),
                port: form.receipt.printer.port,
              }
            : undefined,
        },
        registerIdleTimeoutSeconds: form.registerIdleTimeoutSeconds,
        ...(reason.trim() ? { reason: reason.trim() } : {}),
      };
      return unwrap(await api.v1.outlets({ outletId: props.outlet.id }).patch(body));
    },
    onSuccess: async (updated) => {
      await storeOutlet(queryClient, updated);
      setForm(updated);
      setReason("");
      toast.success(t("outlets.saved"));
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    save.mutate();
  }

  return (
    <form className="flex flex-col gap-6" onSubmit={submit} noValidate>
      <fieldset disabled={!props.editable} className="contents">
        <Section title={t("outlets.identity")} description={t("outlets.identityHint")}>
          <Field label={t("outlets.name")} hint={t("outlets.nameHint")}>
            {(id) => <Input id={id} value={form.name} onChange={(e) => set("name", e.target.value)} />}
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t("outlets.phone")}>
              {(id) => (
                <Input
                  id={id}
                  type="tel"
                  inputMode="tel"
                  value={form.phone ?? ""}
                  onChange={(e) => set("phone", e.target.value)}
                />
              )}
            </Field>
            <Field label={t("outlets.email")}>
              {(id) => (
                <Input
                  id={id}
                  type="email"
                  inputMode="email"
                  value={form.email ?? ""}
                  onChange={(e) => set("email", e.target.value)}
                />
              )}
            </Field>
          </div>
          <Field label={t("outlets.address")}>
            {(id) => (
              <Input id={id} value={form.address ?? ""} onChange={(e) => set("address", e.target.value)} />
            )}
          </Field>
        </Section>

        <Section title={t("outlets.hours")} description={t("outlets.hoursHint")}>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t("outlets.timezone")}>
              {(id) => (
                <OptionSelect
                  id={id}
                  options={
                    // Outlets start in an Indonesian zone, but settings accept any IANA zone the API does.
                    TIMEZONE_OPTIONS.some((zone) => zone.value === form.timezone)
                      ? TIMEZONE_OPTIONS
                      : [{ value: form.timezone, label: form.timezone }, ...TIMEZONE_OPTIONS]
                  }
                  value={form.timezone}
                  onValueChange={(timezone) => set("timezone", timezone)}
                  disabled={readOnly}
                />
              )}
            </Field>
            <Field label={t("outlets.cutoff")} hint={t("outlets.cutoffHint")}>
              {(id) => (
                <Input
                  id={id}
                  type="time"
                  value={form.businessDayCutoff}
                  onChange={(e) => set("businessDayCutoff", e.target.value)}
                />
              )}
            </Field>
          </div>
          <div className="flex flex-col gap-2">
            <p className="font-medium text-sm">{t("outlets.openingHours")}</p>
            {WEEKDAYS.map((key) => {
              const label = t(DAY_KEYS[key]);
              const day = form.openingHours[key];
              return (
                <div
                  key={key}
                  className="grid grid-cols-[5rem_1fr] items-center gap-2 sm:grid-cols-[6rem_8rem_1fr_1fr]"
                >
                  <span className="text-sm">{label}</span>
                  <Label className="min-h-11 font-normal">
                    <Checkbox
                      checked={day !== null}
                      disabled={readOnly}
                      onCheckedChange={(checked) =>
                        setDay(key, checked === true ? { open: "07:00", close: "22:00" } : null)
                      }
                    />
                    {t("outlets.open")}
                  </Label>
                  {day && (
                    <>
                      <Input
                        type="time"
                        aria-label={t("outlets.opensAt", { day: label })}
                        value={day.open}
                        onChange={(e) => setDay(key, { ...day, open: e.target.value })}
                      />
                      <Input
                        type="time"
                        aria-label={t("outlets.closesAt", { day: label })}
                        value={day.close}
                        onChange={(e) => setDay(key, { ...day, close: e.target.value })}
                      />
                    </>
                  )}
                </div>
              );
            })}
            <p className="text-muted-foreground text-xs">{t("outlets.overnightHint")}</p>
          </div>
          <Field label={t("outlets.idleLock")}>
            {(id) => (
              <Input
                id={id}
                type="number"
                inputMode="numeric"
                min={1}
                max={60}
                value={Math.round(form.registerIdleTimeoutSeconds / 60)}
                onChange={(e) => {
                  // An emptied field keeps the last valid value instead of sending 0.
                  const minutes = e.target.valueAsNumber;
                  if (Number.isFinite(minutes)) set("registerIdleTimeoutSeconds", minutes * 60);
                }}
              />
            )}
          </Field>
        </Section>

        <Section title={t("outlets.receipt")} description={t("outlets.receiptHint")}>
          <Field label={t("outlets.paperWidth")} hint={t("outlets.paperWidthHint")}>
            {(id) => (
              <OptionSelect
                id={id}
                options={PAPER_WIDTHS.map((w) => ({ value: w, label: t(`outlets.paper.${w}`) }))}
                value={form.receipt.paperWidth ?? "58"}
                onValueChange={(paperWidth) => setReceipt({ paperWidth: paperWidth as PaperWidth })}
                disabled={readOnly}
              />
            )}
          </Field>
          <Field label={t("outlets.printerHost")} hint={t("outlets.printerHint")}>
            {(id) => (
              <Input
                id={id}
                disabled={readOnly}
                value={form.receipt.printer?.host ?? ""}
                onChange={(e) => {
                  // An emptied host means the outlet prints through the browser again.
                  const host = e.target.value;
                  const current = form.receipt.printer;
                  setReceipt({
                    printer: host.trim() ? { host, port: current?.port ?? 9100 } : undefined,
                  });
                }}
              />
            )}
          </Field>
          <Field label={t("outlets.printerPort")}>
            {(id) => (
              <Input
                id={id}
                type="number"
                inputMode="numeric"
                disabled={readOnly || !form.receipt.printer}
                value={form.receipt.printer?.port ?? 9100}
                onChange={(e) => {
                  const port = e.target.valueAsNumber;
                  if (Number.isInteger(port) && port >= 1 && port <= 65535)
                    setReceipt({ printer: { host: form.receipt.printer?.host ?? "", port } });
                }}
              />
            )}
          </Field>
          <Field label={t("outlets.receiptHeader")}>
            {(id) => (
              <Input
                id={id}
                value={form.receipt.header}
                onChange={(e) => setReceipt({ header: e.target.value })}
              />
            )}
          </Field>
          <Field label={t("outlets.receiptFooter")}>
            {(id) => (
              <Input
                id={id}
                value={form.receipt.footer}
                onChange={(e) => setReceipt({ footer: e.target.value })}
              />
            )}
          </Field>
          <Label className="min-h-11 justify-between font-normal">
            {t("outlets.showAddress")}
            <Switch
              checked={form.receipt.showAddress}
              disabled={readOnly}
              onCheckedChange={(showAddress) => setReceipt({ showAddress })}
            />
          </Label>
          <Label className="min-h-11 justify-between font-normal">
            {t("outlets.showPhone")}
            <Switch
              checked={form.receipt.showPhone}
              disabled={readOnly}
              onCheckedChange={(showPhone) => setReceipt({ showPhone })}
            />
          </Label>
        </Section>

        <Field label={t("outlets.reason")}>
          {(id) => <Input id={id} value={reason} onChange={(e) => setReason(e.target.value)} />}
        </Field>
        {save.error && (
          <p role="alert" className="text-destructive text-sm">
            {errorMessage(t, save.error)}
          </p>
        )}
      </fieldset>
      {props.editable ? (
        <Button type="submit" size="pos" disabled={save.isPending || !form.name.trim()}>
          {t(save.isPending ? "common.saving" : "outlets.saveSettings")}
        </Button>
      ) : (
        <p className="text-muted-foreground text-sm">{t("outlets.readOnly")}</p>
      )}
    </form>
  );
}

/** Owners only: a deactivated outlet keeps its data but its registers and outlet-only staff can't sign in. */
function ActivationCard(props: { outlet: OutletSettings }) {
  const { t } = useMessages(outlets);
  const queryClient = useQueryClient();
  const [reason, setReason] = useState("");
  const reasonId = useId();
  const toggle = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.v1
          .outlets({ outletId: props.outlet.id })
          .active.put({ active: !props.outlet.active, ...(reason.trim() ? { reason: reason.trim() } : {}) }),
      ),
    onSuccess: async (updated) => {
      await storeOutlet(queryClient, updated);
      setReason("");
      toast.success(t(updated.active ? "outlets.reactivated" : "outlets.deactivated"));
    },
  });

  return (
    <Card className="border-destructive/40">
      <CardHeader>
        <CardTitle>{t(props.outlet.active ? "outlets.deactivate" : "outlets.reactivate")}</CardTitle>
        <CardDescription>
          {t(props.outlet.active ? "outlets.deactivateHint" : "outlets.reactivateHint")}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={reasonId}>{t("outlets.activationReason")}</Label>
          <Input id={reasonId} value={reason} onChange={(e) => setReason(e.target.value)} />
        </div>
        {toggle.error && (
          <p role="alert" className="text-destructive text-sm">
            {errorMessage(t, toggle.error)}
          </p>
        )}
        {/* Deactivating signs every register of the outlet out at once. */}
        <ActiveToggle
          active={props.outlet.active}
          title={t("outlets.deactivateTitle", { name: props.outlet.name })}
          description={t("outlets.deactivateConfirm")}
          confirmLabel={t("outlets.deactivate")}
          activateLabel={t("outlets.activate")}
          disabled={toggle.isPending}
          onChange={() => toggle.mutate()}
          className="self-start"
        />
      </CardContent>
    </Card>
  );
}
